import { FC, useState, useEffect, useRef } from 'react'
import throttle from 'lodash.throttle'
import cn from 'clsx'
import { useUI } from '@components/ui/context'
import useIsMobile from '@utils/useIsMobile'
import {
  MenuIcon,
  SearchIcon,
  ShoppingBagIcon,
  CloseIconLarge,
  CaretLeft,
} from '@components/icons'
import { PrimaryLogo } from '@components/ui/Logo'
import NavMenuL2 from '@components/common/NavMenuL2'
import NavMenuL1 from '@components/common/NavMenuL1'
import { enableBodyScroll, disableBodyScroll } from 'body-scroll-lock'

interface NavProps {
  navigation?: any
}

const ns = `site-header`

const Nav: FC<NavProps> = ({ navigation }) => {
  const { openSearch, openSidebar, setSidebarView } = useUI()
  const stickyScrollbar = navigation?.stickyScrollbar || false

  const [hasScrolled, setHasScrolled] = useState(true)
  const [navL1Open, setNavL1Open] = useState(false)
  const [navL2Open, setNavL2Open] = useState(false)
  const [navL2Exists, setNavL2Exists] = useState(false)
  const [currentMenuCategory, setCurrentMenuCategory] = useState(0)

  const isMobile = useIsMobile()
  const navRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (navigation) {
      setNavL2Exists(
        navigation?.primaryNavListCollection.items[currentMenuCategory]
          .__typename === 'MenuCategory'
      )
    }
  }, [
    currentMenuCategory,
    navigation,
    navigation?.primaryNavListCollection?.items,
  ])

  function handleMenuClick() {
    if (navL1Open) {
      setNavL1Open(false)
      enableBodyScroll(navRef.current!)
    } else {
      setNavL1Open(true)
      disableBodyScroll(navRef.current!)
    }
  }

  function handleMenuClose() {
    setNavL1Open(false)
    setNavL2Open(false)
    enableBodyScroll(navRef.current!)
  }

  function handlePrimaryNavMouseOver(e: any) {
    e.preventDefault()
    setCurrentMenuCategory(e.currentTarget.dataset.index)
    setNavL2Open(true)
  }

  function handleBackButtonClick() {
    setNavL1Open(true)
    setNavL2Open(false)
  }

  function handleL2MenuClose() {
    setNavL2Open(false)
  }

  function handleOpenCart() {
    setSidebarView('CART_VIEW')
    openSidebar()
    handleSidebarOpen()
  }

  const handleSearchOpen = () => {
    openSearch()
  }

  const handleSidebarOpen = () => {
    const element = document.getElementById('launcher')
    if (element) {
      element.style.opacity = '0'
      element.style.pointerEvents = 'none'
    }
  }

  return (
    <div
      ref={navRef}
      className={cn(`${ns}__nav-wrapper`, {
        scrolled: hasScrolled,
        sticky: stickyScrollbar,
      })}
    >
      <div className={`${ns}__nav-bar`}>
        <div
          className={cn(`${ns}__menu-icon`, {
            scrolled: hasScrolled,
            sticky: stickyScrollbar,
          })}
          onClick={handleMenuClick}
        >
          <MenuIcon />
        </div>
        <a
          href={`/`}
          className={cn(`${ns}__logo`, {
            scrolled: hasScrolled,
            sticky: stickyScrollbar,
          })}
        >
          <PrimaryLogo />
        </a>
        <div className={`${ns}__quick-links`}>
{/*
          <div
            className={cn(`${ns}__search-icon`, {
              scrolled: hasScrolled,
              sticky: stickyScrollbar,
            })}
            onClick={handleSearchOpen}
          >
            <SearchIcon />
          </div>
*/}
          <div
            onClick={handleOpenCart}
            className={cn(`${ns}__shopping-bag-icon`, {
              scrolled: hasScrolled,
              sticky: stickyScrollbar,
            })}
          >
            <ShoppingBagIcon />
          </div>
        </div>
      </div>
      {!isMobile && (navL1Open || navL2Open) && (
        <div className="nav-background" onClick={handleMenuClose}></div>
      )}
      <>
        {navL1Open && (
          <div
            className={cn(`${ns}__nav-menu-l1__close-icon-wrapper-mobile`, {
              'close-icon-margin-small': !navL2Open,
            })}
          >
            <div onClick={handleMenuClose}>
              <CloseIconLarge />
            </div>
            {navL2Open && (
              <button
                className={`nav-menu-l2__back-button`}
                onClick={handleBackButtonClick}
              >
                <div className={`nav-menu-l2__back-button__back-caret`}>
                  <CaretLeft width={12} height={12} color={'grey-500'} />
                </div>
                <p>Back</p>
              </button>
            )}
          </div>
        )}
        {navigation && (
          <NavMenuL1
            navigation={navigation}
            closeMenu={handleMenuClose}
            primaryNavMouseOver={handlePrimaryNavMouseOver}
            isOpen={navL1Open}
            closeL2Menu={handleL2MenuClose}
          />
        )}
        {navL2Exists && (
          <NavMenuL2
            menuItem={
              navigation?.primaryNavListCollection.items[currentMenuCategory]
            }
            isMobile={isMobile}
            closeMenu={handleMenuClose}
            isOpen={navL2Open}
            backButtonClick={handleBackButtonClick}
            closeL2Menu={handleL2MenuClose}
            categoryId={currentMenuCategory}
          />
        )}
      </>
    </div>
  )
}

export default Nav
